/**
 * Pure page-type selection for the GTM `ecomm_pagetype` / `pageType` pushes.
 *
 * Three surfaces have to agree on which string a given render represents:
 *   - `Layout`'s SSR inline push (`PageTypeInlineScript`),
 *   - `PageTypeTracker`'s router subscriber (SPA nav, synchronous with
 *     `pushState` — see SFCC-11888),
 *   - `PageTypeTracker`'s layout-effect safety net.
 *
 * They read the same data from three different shapes (`useMatches()`, raw
 * router state, root loader data), so the *decisions* live here as dep-free
 * functions: root.tsx cannot be imported under vitest, and a divergence
 * between the SSR and SPA answers is exactly the class of bug that produced
 * the stale-pageType incident in SFCC-11888.
 *
 * SFCC-12034 C7.
 */

/** Minimal shape of a route match — `useMatches()` and router state both fit. */
export type PageTypeMatch = { data?: unknown };

/**
 * Deepest match whose loader data carries a non-empty string `pageType`.
 *
 * **Loader contract (SFCC-10645)**: page-rendering loaders must `await
 * resolvePageType(...)` and return the resolved string at the top level as
 * `pageType`. A deferred `Promise<string>` won't be picked up — the
 * `typeof === "string"` guard rejects it and the scan falls through to a less
 * specific ancestor (or to `undefined`).
 */
export function matchedPageType(
  matches: readonly PageTypeMatch[],
): string | undefined {
  for (let i = matches.length - 1; i >= 0; i--) {
    const d = matches[i]?.data as { pageType?: unknown } | undefined;
    if (typeof d?.pageType === "string" && d.pageType.length > 0) {
      return d.pageType;
    }
  }
  return undefined;
}

/** Non-empty-string narrowing for values read out of untyped loader data. */
function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * The root loader's `other`-bucket fallback (`resolvePageType('other')`),
 * read defensively out of raw loader data.
 */
export function fallbackFromRootData(rootData: unknown): string | undefined {
  return asNonEmptyString(
    (rootData as { fallbackPageType?: unknown } | undefined)?.fallbackPageType,
  );
}

/** The root loader's `notfound` type, read defensively out of raw loader data. */
export function notFoundFromRootData(rootData: unknown): string | undefined {
  return asNonEmptyString(
    (rootData as { notFoundPageType?: unknown } | undefined)?.notFoundPageType,
  );
}

/**
 * The error for this render, wherever it was caught.
 *
 * React Router keys `state.errors` by the **boundary** route id, and
 * `useRouteError()` only looks up the CURRENT route's id
 * (`state.errors?.[routeId]`). Read from root — where both `Layout` and
 * `PageTypeTracker` live — that means a route with its own `ErrorBoundary`
 * reports no error at all, even on a 404. Most content routes have one
 * (learning-center, PLP), so keying page-type off `useRouteError()` alone
 * would classify their 404s as ordinary pageviews. Scanning the whole map
 * catches child and root boundaries alike.
 */
export function boundaryError(
  errors: Record<string, unknown> | null | undefined,
): unknown {
  if (!errors) return undefined;
  return Object.values(errors)[0];
}

/**
 * `layoutPageType` applied to raw router-subscriber state, so the SPA answer is
 * the same function as the SSR answer rather than a parallel reimplementation
 * of it — agreement by construction, not by convention.
 *
 * `is404` is injected so this module stays dep-free (its reason for existing):
 * the caller supplies React Router's `isRouteErrorResponse` check.
 */
export function pageTypeFromRouterState(
  state: {
    matches: { route: { id: string } }[];
    loaderData: Record<string, unknown>;
    errors?: Record<string, unknown> | null;
  },
  is404: (error: unknown) => boolean,
): string | undefined {
  const error = boundaryError(state.errors);
  const rootData = state.loaderData.root;
  return layoutPageType({
    isError: error !== undefined,
    is404: error !== undefined && is404(error),
    matched: matchedPageType(
      state.matches.map(m => ({ data: state.loaderData[m.route.id] })),
    ),
    notFoundPageType: notFoundFromRootData(rootData),
    fallbackPageType: fallbackFromRootData(rootData),
  });
}

/**
 * The page type `Layout` pushes at first paint.
 *
 * - **404** → the root loader's `notfound` type. Unchanged from SFCC-10645.
 * - **Any other error** → whatever the matches carried, with NO fallback
 *   injected. A 5xx or generic crash must not masquerade as an ordinary
 *   "Other Page" view; leaving it unset keeps the absence visible.
 * - **Normal render** → the matched type, falling back to the `other` bucket
 *   (SFRA `processDL` parity, `dataLayerHelper.js:811-821`).
 */
export function layoutPageType(input: {
  isError: boolean;
  is404: boolean;
  matched: string | undefined;
  notFoundPageType: string | undefined;
  fallbackPageType: string | undefined;
}): string | undefined {
  if (input.isError) {
    return input.is404 ? input.notFoundPageType : input.matched;
  }
  return input.matched ?? input.fallbackPageType;
}
